import { DateTimeUtils } from '@servicetitan/datetime-utils';
import { BodyText, BodyTextPropsStrict, Icon } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { FC } from 'react';
import { getAppointmentWindowText, getDateText, getDisplayOption } from './helpers';

export enum AppointmentState {
    Scheduled = 'scheduled',
    InProgress = 'inProgress',
    Completed = 'completed',
    CanceledOrOnHold = 'canceledOrOnHold',
}

interface AppointmentWindowProps {
    appointmentTimeOptions: AppointmentTimeOptionsSettings;
    appointmentState: AppointmentState;
    startDate?: Date;
    endDate?: Date;
    arrivalWindowPlanStartDate?: Date;
    arrivalWindowPlanEndDate?: Date;
    className?: string;
    el?: any;
    inline?: boolean;
    size?: BodyTextPropsStrict['size'];
    subdued?: boolean;
    bold?: boolean;
    withIcon?: boolean;
    upcoming?: boolean;
}

export const AppointmentWindow: FC<AppointmentWindowProps> = observer(
    ({
        appointmentState,
        appointmentTimeOptions,
        arrivalWindowPlanEndDate,
        arrivalWindowPlanStartDate,
        bold,
        className,
        el,
        endDate,
        inline,
        size,
        startDate,
        subdued,
        upcoming,
        withIcon,
    }) => {
        const [dateTimeUtils] = useDependencies(DateTimeUtils);

        const dateWindowText = () => {
            const displayOption = getDisplayOption(appointmentState, appointmentTimeOptions);

            switch (displayOption) {
                case AppointmentTimeOptionStatus.DateOnly:
                    return getDateText(dateTimeUtils, startDate);
                case AppointmentTimeOptionStatus.StartAndEndTime:
                    return getAppointmentWindowText(dateTimeUtils, startDate, endDate);
                case AppointmentTimeOptionStatus.ArrivalWindow: {
                    if (arrivalWindowPlanStartDate && arrivalWindowPlanEndDate) {
                        return getAppointmentWindowText(
                            dateTimeUtils,
                            arrivalWindowPlanStartDate,
                            arrivalWindowPlanEndDate
                        );
                    }
                    return getDateText(dateTimeUtils, startDate);
                }
            }
        };

        const text = dateWindowText();

        if (!text) {
            return null;
        }

        return (
            <BodyText
                bold={bold}
                size={size}
                subdued={subdued}
                inline={inline}
                el={el}
                className={className}
            >
                {withIcon && <Icon name="event" size={16} className="m-r-half m-b-half" />}
                {upcoming && 'Upcoming appointment: '}
                {text}
            </BodyText>
        );
    }
);

export enum AppointmentTimeOptionStatus {
    StartAndEndTime = 0,
    ArrivalWindow = 1,
    DateOnly = 2,
}

export interface AppointmentTimeOptionsSettings {
    scheduled: { selection: AppointmentTimeOptionStatus };
    inProgress: { selection: AppointmentTimeOptionStatus };
    completed: { selection: AppointmentTimeOptionStatus };
}
